import * as React from 'react'
import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { UpdateGroupedTimesheetMutationPayload } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { generateUUID } from '@/modules/shared/utils/id'
import { requestPrecalculateDrivers } from '@/lib/api/company/timesheet/calculate/requestPrecalculateDrivers'
import { requestCalculateDrivers } from '@/lib/api/company/timesheet/calculate/requestCalculateDrivers'
import { waitForImportTask } from '@/lib/api/config/importTask/status/waitForImportTask'
import { getGroupedTimesheet } from '@/lib/api/company/timesheet/groupedTimesheet/getGroupedTimesheet'
import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { toast } from '@/lib/toasts/api'
import { hasOutOfDateQuarterRateNotice } from '../../useOutOfDateRateConfirm'
import { notifyTaxExemptNotices } from '../../taxExemptNotice'
import axios from 'axios'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TaskType } from '@/app/constants/taskType'
import { startTrackedTask } from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import { getNettoMismatchForDisplay, rozniSie } from '../utils'
export type SaveAndCalculateFlowStatus =
  | 'awaiting'
  | 'runing-saving'
  | 'runnning-precalculate'
  | 'running-calculate'
  | 'running-status'
  | 'running-adopt'
  | 'finish-with-error'
  | 'finish-with-success'

const statusTextConfigKeys: Record<SaveAndCalculateFlowStatus, string> = {
  awaiting: '',
  'runing-saving': 'timesheet.status-saving',
  'runnning-precalculate': 'timesheet.status-precalculating',
  'running-calculate': 'timesheet.status-calculating',
  'running-status': 'timesheet.status-calculating',
  'running-adopt': 'timesheet.status-adopting-rewards',
  'finish-with-error': '',
  'finish-with-success': '',
}

// /timesheet/precalculate zwraca liste OperationResult. Kazdy wynik inny niz Success
// daje 400 (SupertachoResult.OnFinish) i przerywa przeliczenie - dlatego informacje,
// ktore niczego nie blokuja, przychodza jako Success z wlasnym kodem.
// Rezygnacja uzytkownika w dialogu - nie jest bledem, wiec nie moze wyladowac w
// dymku "Wystapil blad podczas przeliczania ewidencji".
class ForeignTripCancelled extends Error {}

type PrecalculateNotice = { code: string; message: string }

const collectPrecalculateNotices = (payload: any): PrecalculateNotice[] => {
  const items = Array.isArray(payload) ? payload : payload ? [payload] : []
  return items
    .map((item: any) => ({
      code: String(item?.code ?? ''),
      message: String(item?.message ?? ''),
    }))
    .filter((item: PrecalculateNotice) => item.code !== '')
}

type MutateArgs = UpdateGroupedTimesheetMutationPayload & {
  automaticOvertimeCompensation: boolean
  zusPaidFromAverageGross: boolean
}

type Props = {
  companyId: string
  month: number
  year: number
  // Must match the grid's query param so the rows fetched here are reusable as grid
  // state — otherwise the view would need its own refetch (a second identical
  // groupedTimesheet call at the end of every calculation).
  domesticBusinessTripDateType?: 'start' | 'end'
  // freshRows: final grid rows this flow already fetched (null when the read failed) —
  // the view merges them instead of refetching.
  onSuccess?: (freshRows: PayrollDriverRow[] | null) => void
  onError?: (err: unknown) => void
  /**
   * Pytanie o kierowcow z aktywnosciami zagranicznymi bez delegacji na liczony miesiac.
   * `false` przerywa przeliczenie. Bez tego callbacku zostaje sam dymek.
   */
  confirmForeignActivityWithoutTrip?: (driverNames: string) => Promise<boolean>
  /**
   * Pytanie o liczenie po nieaktualnym kursie kwartalnym. Bez tego callbacku brak
   * kursu przerywa przeliczenie - cichej zgody nie zakladamy (parytet z WPF).
   */
  confirmOutOfDateExchangeRate?: () => Promise<boolean>
}

const DEFAULT_ERROR_MSG_KEY = 'timesheet.calculation-error'

// TEST: wylacznik domykania nagrod (przebieg 2). Przy false "Przelicz" robi wylacznie
// przebieg 1 i na ewidencji zostaje nagroda z gridu.
// Docelowo do usuniecia albo do przeniesienia w konfiguracje.
const ADOPT_REWARDS_ENABLED = true

// Nagrode domyka teraz SAM JOB (flaga adoptComputedRewards w /timesheet/calculate):
// po wyliczeniu propozycji przelicza kierowce ponownie z nalozona nagroda i zapisuje
// RAZ — czyli dokladnie to, co robil tu przebieg 2, minus drugi precalculate, drugi
// zapis i drugie odpytywanie o status. Przebieg 2 zostaje w kodzie jako awaryjna
// sciezka zgodnosci ze starszym fileprocessorem (ustaw false, by go przywrocic).
const ADOPT_IN_JOB = true

// Przebieg 3 - cofanie nagrody tam, gdzie po nalozeniu wiersz dalej nie domyka sie do
// oczekiwanego netto (nalozona wartosc jest wtedy awaryjna, nie propozycja).
// WYLACZONY decyzja: "Przelicz" robi wylacznie przebiegi 1 i 2 - nalozona propozycja zostaje
// na ewidencji, a niedomkniete wiersze sa tylko oznaczane w gridzie (getNettoMismatch).
// Dotyczy obu ekranow, bo oba ida tym samym przeplywem.
//
// Caly przebieg jest ZAKOMENTOWANY (ten wylacznik i blok w mutationFn nizej) - zostaje w
// kodzie, bo decyzja moze wrocic. Zeby go przywrocic: odkomentowac oba miejsca, ustawic
// wylacznik na true i dolozyc revertFailed z powrotem do passIncomplete.
// const REVERT_UNCLOSED_REWARDS_ENABLED = false

export function useSaveAndCalculate(props: Props) {
  const [status, setStatus] =
    React.useState<SaveAndCalculateFlowStatus>('awaiting')
  const [progress, setProgress] = React.useState<{
    current: number
    total: number
  } | null>(null)
  // Bez tego licznik postepu po dojsciu do N/N wraca do 0/M i wyglada jak zapetlenie.
  const [isAdoptPass, setIsAdoptPass] = React.useState(false)
  const { tSafe } = useLocalize()

  // Zwraca false, gdy uzytkownik odmowil - wtedy przeliczenie sie nie odbywa.
  const confirmPrecalculateNotices = React.useCallback(
    async (payload: any): Promise<boolean> => {
      // Po decyzji w dialogu - przy "Anuluj" nic sie nie liczy, wiec "przelicz ponownie" nie ma sensu.
      const notifyTaxExempt = () =>
        notifyTaxExemptNotices(payload, (key) => tSafe(key, key))

      const names = collectPrecalculateNotices(payload)
        .filter(
          (notice) =>
            notice.code === 'TIMESHEET_FOREIGN_ACTIVITY_WITHOUT_TRIP' &&
            notice.message !== '',
        )
        // Rekordy rozdziela nowa linia, nie przecinek - nazwiska i listy krajow
        // same zawieraja przecinki.
        .map((notice) => notice.message)
        .join('\n')

      if (!names) {
        notifyTaxExempt()
        return true
      }

      if (props.confirmForeignActivityWithoutTrip) {
        const confirmed = await props.confirmForeignActivityWithoutTrip(names)
        if (confirmed) notifyTaxExempt()
        return confirmed
      }

      // Szczegoly siedza w formacie "nazwisko|dni|zakres|kraje" - w dymku zostaja
      // same nazwiska.
      const plainNames = names
        .split(/\r?\n/)
        .map((line) => line.split('|')[0].trim())
        .filter((name) => name !== '')
        .join(', ')

      toast.warning({
        title: tSafe('timesheet.force-calculation-title-warning'),
        text: `"${plainNames}" ${tSafe('timesheet.foreign-activity-without-trip')}`,
      })
      notifyTaxExempt()
      return true
    },
    [tSafe, props.confirmForeignActivityWithoutTrip],
  )
  const queryClient = useQueryClient()
  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes('group-timesheet'),
    })
    // Przeliczenie kasuje flagi nieaktualnosci, wiec znaczniki zadan przy kierowcach
    // musza zniknac razem z nim.
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'driverRequiredActions',
    })
  }

  const m = useMutation({
    mutationKey: [
      'chained-save-and-calculate',
      props.companyId,
      props.month,
      props.year,
    ],
    mutationFn: async (args: MutateArgs) => {
      const driverIds = args.drivers.map((d) => d.id)
      setIsAdoptPass(false)

      // The calculate job persists the whole bonus configuration itself (rates,
      // addition-for-leaves, bailiff, benefits, "Wliczaj", expected netto), so this
      // flow no longer runs the synchronous PATCH saveBonusConfiguration + the
      // groupedTimesheet refetch first — for ~200 drivers those two calls took ~30s
      // before the job even started. The grid state is the source of truth here:
      // GroupPayrollView builds each bonusConfiguration from the loaded query data
      // merged with the user's edits (incl. timesheetAdditionForLeaves and
      // bailiffDeductionAmount, which the removed refetch used to backfill).
      // Buduje konfiguracje przebiegu 1 (z kwotami z gridu). Przebieg 2 sklada wlasne, z nagroda
      // podmieniona na wyliczona - dlatego kwota wpisana recznie w kolumnie NAGRODA zostaje
      // zastapiona. To swiadoma regula tego ekranu: nagroda domyka wyplate do oczekiwanego netto,
      // a kwota sterujaca jest OCZEKIWANE NETTO.
      const buildBonusConfigs = (clientDrivers: MutateArgs['drivers']) => {
        return clientDrivers.map((d) => ({
          ...d.bonusConfiguration,
          rewardRate: d.bonusConfiguration.rewardRate ?? 0,
          useValuesFromContract: false,
        }))
      }

      // isAdopt przestawia etykiete na caly czas trwania przebiegu 2. Samo ustawienie statusu
      // przed wywolaniem nic nie daje - pierwszy setStatus w srodku i tak by je natychmiast
      // nadpisal, zanim React zdazy cokolwiek narysowac.
      // Zgoda pada raz na cale wywolanie - przebieg awaryjny nie pyta drugi raz.
      let outOfDateAccepted = false

      const runCalculatePass = async (bonusConfigs: any[], passDriverIds: number[], isAdopt = false) => {
        const taskId = generateUUID()

        setStatus(isAdopt ? 'running-adopt' : 'runnning-precalculate')
        // Pierwszy przebieg BEZ zgody na starszy kurs kwartalny - przy `true` serwer w
        // ogole nie zglosi jego braku. Ostrzezenie wraca jako 400, wiec lapiemy je z
        // wyjatku, pytamy i powtarzamy precalculate juz ze zgoda. Parytet z WPF.
        const runPrecalculate = (accepted: boolean) =>
          requestPrecalculateDrivers({
            companyId: props.companyId,
            drivers: passDriverIds,
            id: taskId,
            months: [props.month],
            outOfDateQuarterExchangeRateAccepted: accepted,
            year: props.year,
            automaticOvertimeCompensation: args.automaticOvertimeCompensation,
            zusPaidFromAverageGross: args.zusPaidFromAverageGross,
          })

        let precalculateResponse
        try {
          precalculateResponse = await runPrecalculate(outOfDateAccepted)
        } catch (precalculateError) {
          if (
            outOfDateAccepted ||
            !props.confirmOutOfDateExchangeRate ||
            !hasOutOfDateQuarterRateNotice(precalculateError)
          ) {
            throw precalculateError
          }

          if (!(await props.confirmOutOfDateExchangeRate())) {
            throw new ForeignTripCancelled()
          }
          outOfDateAccepted = true
          precalculateResponse = await runPrecalculate(true)
        }

        // Ostrzezenia z precalculate (np. aktywnosci zagraniczne bez delegacji) ten ekran
        // do tej pory wyrzucal do kosza - odpowiedz byla otypowana jako pusta. Siatka
        // premii i nagrody jest wlasnie tym miejscem, w ktorym skutek widac (zawyzona
        // nagroda i brutto), wiec pokazujemy je tak samo jak ekran ewidencji.
        // Tylko przebieg 1: przebieg awaryjny (isAdopt) powtorzylby te same dymki.
        if (!isAdopt) {
          const confirmed = await confirmPrecalculateNotices(
            precalculateResponse?.data,
          )
          if (!confirmed) {
            throw new ForeignTripCancelled()
          }
        }

        setStatus(isAdopt ? 'running-adopt' : 'running-calculate')
        // Register with the tracker as well as this screen's own poller. Without it
        // the task exists only inside this component: a refresh loses it, and the
        // cross-user banner shows the user their OWN calculation as if a colleague
        // had started it (the tracker only learns about it from the server, where it
        // has no owner marked). The local poller below still drives this modal's
        // progress — this only makes the task known to the rest of the app.
        const { taskId: runningTaskId, adopted } = await startTrackedTask({
          companyId: props.companyId,
          type: TaskType.Timesheet,
          taskId,
          scopeKey: taskScopeKey.timesheet(props.companyId, props.year, [
            props.month,
          ]),
          progressTotal: passDriverIds.length,
          start: () =>
            requestCalculateDrivers({
              bonusConfigurations: bonusConfigs,
              companyId: props.companyId,
              drivers: passDriverIds,
              id: taskId,
              months: [props.month],
              outOfDateQuarterExchangeRateAccepted: outOfDateAccepted,
              year: props.year,
              automaticOvertimeCompensation: args.automaticOvertimeCompensation,
              zusPaidFromAverageGross: args.zusPaidFromAverageGross,
              // Tylko przebieg 1: job sam domyka nagrody. Awaryjny przebieg 2 (isAdopt)
              // nie prosi o adopcje drugi raz.
              adoptComputedRewards:
                ADOPT_IN_JOB && ADOPT_REWARDS_ENABLED && !isAdopt,
            }),
        })

        if (adopted) {
          toast.info({
            title: tSafe(
              'timesheet.calculation-in-progress',
              'Trwa przeliczanie ewidencji...',
            ),
            text: tSafe(
              'timesheet.calculation-adopted-other-selection',
              'Ten miesiąc jest już przeliczany. Śledzimy tamto zadanie — Twoje zaznaczenie nie zostało do niego dodane.',
            ),
          })
        }

        setStatus(isAdopt ? 'running-adopt' : 'running-status')
        setProgress(null)
        try {
          // Shared poller (same as the delete flow): timeout + tolerance for
          // dropped status requests, so a single 502 on a poll no longer fails a
          // calculation that keeps running server-side.
          // runningTaskId, nie taskId: przy adopcji serwer oddaje ID zadania, ktore
          // JUZ leci, a nasz lokalny GUID nigdy nie powstal. Odpytywanie go konczylo
          // sie serią 404 az do timeoutu, zamiast sledzenia realnego przebiegu.
          await waitForImportTask({ taskId: runningTaskId, onProgress: setProgress })
        } finally {
          setProgress(null)
        }
      }

      // Przebieg 1: silnik liczy kwoty dla nagrody, ktora jest teraz na ewidencji, i przy okazji
      // wylicza propozycje nagrody domykajacej wyplate do oczekiwanego netto. Przebieg 2 naklada
      // te propozycje - solver szuka jej na KOPII ewidencji, wiec bez drugiego przejscia zapisane
      // netto i brutto opisuja poprzednia nagrode i grid pokazuje niespojna trojke (oczekiwane
      // netto / wyliczone netto / nagroda). Robimy to za uzytkownika ta sama sciezka, ktora
      // wykonywalo reczne drugie klikniecie - bez nowego kodu ruszajacego kwoty wyplat.
      await runCalculatePass(buildBonusConfigs(args.drivers), driverIds)

      // Wspolny odczyt gridu po przebiegu. Odpowiedz spoza kontraktu (puste cialo,
      // inny ksztalt) ani nieudany odczyt nie moga wywalic przeliczenia, ktore juz
      // sie zapisalo - oba przypadki zwracaja null. Parametry musza byc identyczne
      // z useGroupedPayroll, inaczej wiersze nie nadaja sie do merge w gridzie.
      const readRows = async (failureLog: string): Promise<PayrollDriverRow[] | null> => {
        try {
          const response = await getGroupedTimesheet({
            companyId: props.companyId,
            driversIds: driverIds,
            year: props.year,
            month: props.month,
            showBonusConfiguration: true,
            domesticBusinessTripDateType: props.domesticBusinessTripDateType,
          })
          return Array.isArray(response.data) ? response.data : null
        } catch (e) {
          console.error(failureLog, e)
          return null
        }
      }

      // Odczyt sluzy wylacznie do wyboru kierowcow do przebiegu 2.
      const rowsAfterFirstPass = await readRows(
        'Nie udało się odczytać wyników po pierwszym przeliczeniu',
      )

      const rowByDriverId = new Map(
        (rowsAfterFirstPass ?? []).map((row) => [row.driver.id, row]),
      )

      // rozniSie zyje teraz w ../utils (wspolne z gridem) - tolerancja i semantyka bez zmian,
      // bo od tego zalezy, ktorzy kierowcy ida do przebiegu adopcji, czyli KWOTY.

      // Przy powtornym przeliczeniu tego samego miesiaca ta lista jest zwykle pusta i przebieg 2
      // w ogole nie startuje.
      const toAdopt = !ADOPT_REWARDS_ENABLED || ADOPT_IN_JOB ? [] : args.drivers.flatMap((d) => {
        // Kierowca zaznaczony w panelu, ale bez zaladowanego wiersza, dostaje pusta konfiguracje
        // bez driverId - silnik dopasowuje konfiguracje wlasnie po tym polu.
        if (d.bonusConfiguration.driverId == null) return []

        const row = rowByDriverId.get(d.id)
        // Zero to sentinel "solver sie poddal" (GrossSalaryCarlculator.Calculate zwraca wtedy
        // wartosc awaryjna, dla krajowca 0), nieodrozninalny od prawdziwej propozycji zerowej.
        // Nalozenie go skasowaloby nagrode, ktora kierowca juz ma - dlatego pomijamy.
        if (!row || !row.calculatedBonus) return []
        if ((row.expectedNettoAmount ?? 0) <= 0) return []

        // Czesciowa ochrona przed nalozeniem nagrody z POPRZEDNIEGO przeliczenia: job potrafi
        // zglosic sukces mimo bledu u pojedynczego kierowcy, a wtedy na ewidencji zostaje stara
        // propozycja. Wylapujemy to tylko przy zmienionym oczekiwanym netto - przy niezmienionym
        // taki wiersz jest nieodroznialny od poprawnego.
        const sentExpectedNetto = d.bonusConfiguration.expectedNettoAmount
        if (
          sentExpectedNetto != null &&
          rozniSie(row.expectedNettoAmount ?? 0, sentExpectedNetto)
        ) {
          return []
        }

        if (!rozniSie(row.reward ?? 0, row.calculatedBonus)) return []

        return [
          {
            id: d.id,
            // Nagroda sprzed adopcji - do cofniecia, gdy nalozona propozycja okaze sie
            // wartoscia awaryjna solvera (patrz przebieg 3 nizej).
            previousReward: row.reward ?? 0,
            config: {
              ...d.bonusConfiguration,
              rewardRate: row.calculatedBonus,
              useValuesFromContract: false,
            },
          },
        ]
      })

      let adoptFailed = false
      let adoptError: unknown = null
      if (toAdopt.length > 0) {
        setIsAdoptPass(true)
        try {
          await runCalculatePass(
            toAdopt.map((t) => t.config),
            toAdopt.map((t) => t.id),
            true,
          )
        } catch (e) {
          console.error('Drugi przebieg (domykanie nagród) nie powiódł się', e)
          adoptFailed = true
          // Komunikat serwera (np. kod bledu walidacji) jest jedyna informacja, z ktora
          // uzytkownik moze cokolwiek zrobic - inaczej klika w kolko to samo.
          adoptError = e
        }
      }

      // Ile wierszy nie domknelo sie do oczekiwanego netto. Grid i tak oznacza je trwale
      // (getNettoMismatch w wierszu) - to jest tylko licznik, zeby uzytkownik wiedzial, ze ma
      // gdzie patrzec, gdy kierowcow sa setki.
      //
      // Bez dodatkowego zapytania w typowym przypadku: gdy przebieg adopcji nie ruszyl (czyli
      // zwykle, przy powtornym przeliczaniu tego samego miesiaca), rowsAfterFirstPass sa juz
      // stanem koncowym. Doczytujemy tylko wtedy, gdy adopcja faktycznie cos zmienila.
      let rowsForMismatchCheck = rowsAfterFirstPass
      if (toAdopt.length > 0 && !adoptFailed) {
        // Nieudany odczyt to nie powod, zeby przeliczenie zglosic jako blad - zostaje
        // sam znacznik w gridzie.
        rowsForMismatchCheck = await readRows(
          'Nie udało się odczytać wyników po domykaniu nagród',
        )
      }

      // Przebieg 3: cofniecie nalozonej nagrody tam, gdzie nic nie domknela.
      //
      // Gdy solver nie potrafi zejsc do oczekiwanego netto, zwraca wartosc awaryjna
      // (bonusForEuAddition w GrossSalaryCarlculator.Calculate) - dla kierowcy krajowego jest
      // to 0 i lapie ja warunek przy budowaniu toAdopt, ale dla kierowcy z doplata za prace w
      // UE jest niezerowa i nie da sie jej odroznic od prawdziwej propozycji. Da sie natomiast
      // sprawdzic PO nalozeniu: jesli wiersz dalej nie domyka sie do oczekiwanego netto, to
      // nalozona nagroda niczego nie domknela i wracamy do nagrody sprzed adopcji.
      //
      // Wyplata sie przez to nie zmienia: w tej strefie netto nie reaguje na nagrode, bo kazda
      // zlotowka nagrody wypycha zlotowke doplaty za prace w UE (zmierzone: nagroda 380,21 ->
      // netto 2588,18, nagroda 197,86 -> doplata UE rosnie o te sama kwote, netto 2588,16).
      // Zmienia sie tylko to, ze do listy plac i eksportow nie trafia liczba udajaca
      // domkniecie - kierowcy zostaje nagroda, ktora faktycznie mial.
      /* ZAKOMENTOWANE - przebieg 3, patrz REVERT_UNCLOSED_REWARDS_ENABLED wyzej.
      let revertFailed = false
      if (REVERT_UNCLOSED_REWARDS_ENABLED && toAdopt.length > 0 && !adoptFailed) {
        const rowAfterAdoptById = new Map(
          (rowsForMismatchCheck ?? []).map((row) => [row.driver.id, row]),
        )

        const toRevert = toAdopt.flatMap((t) => {
          const row = rowAfterAdoptById.get(t.id)
          // Bez odczytu stanu po adopcji nie wiemy, czy domknelo - nie ruszamy kwot w ciemno.
          if (!row) return []
          if (getNettoMismatch(row) == null) return []
          // Nagroda i tak wrocila do wartosci sprzed adopcji (albo jej nie zmienilismy).
          if (!rozniSie(row.reward ?? 0, t.previousReward)) return []

          return [
            {
              id: t.id,
              config: { ...t.config, rewardRate: t.previousReward },
            },
          ]
        })

        if (toRevert.length > 0) {
          try {
            await runCalculatePass(
              toRevert.map((r) => r.config),
              toRevert.map((r) => r.id),
              true,
            )
            rowsForMismatchCheck = await readRows(
              'Nie udało się odczytać wyników po cofnięciu niedomykających nagród',
            )
          } catch (e) {
            console.error(
              'Trzeci przebieg (cofnięcie niedomykających nagród) nie powiódł się',
              e,
            )
            revertFailed = true
          }
        }
      }
      */

      const nettoMismatchCount = (rowsForMismatchCheck ?? []).filter(
        // Licznik w banerze musi zgadzac sie z tym, co widac w gridzie - stad wersja
        // wyswietlaniowa, a nie ta sterujaca kwotami.
        (row) => getNettoMismatchForDisplay(row) != null,
      ).length

      setStatus('finish-with-success')
      // Ostrzegamy WYLACZNIE gdy bylo co domknac i sie nie udalo. Nieudany odczyt przy pustej
      // liscie do domkniecia (albo pominiety pojedynczy wiersz) to nie jest powod, zeby kazac
      // uzytkownikowi przeliczac cala firme od nowa - rozjazd i tak widac w gridzie.
      // rows: stan koncowy odczytany juz w tym przebiegu - grid nakłada go przez setData
      // zamiast robic wlasny, identyczny refetch groupedTimesheet. Po NIEUDANYM przebiegu
      // adopcji rowsAfterFirstPass to stan SPRZED adopcji, a czesc kierowcow mogla juz
      // zostac zapisana — wtedy zwracamy null i grid robi pelny refetch.
      // Po przywroceniu przebiegu 3 dochodzi tu revertFailed (traktowany jak adoptFailed:
      // czesc kierowcow moze miec nalozona wartosc awaryjna, a czesc juz cofnieta - grid musi
      // sie doczytac sam, a uzytkownik dostac ostrzezenie, ze przebieg nie dokonczyl).
      const passIncomplete = adoptFailed
      return { partialWarning: passIncomplete, adoptError, nettoMismatchCount, rows: passIncomplete ? null : (rowsForMismatchCheck ?? null) }
    },
    onError: (e: unknown) => {
      // Uzytkownik sam zrezygnowal w dialogu - zadnego dymka o bledzie.
      if (e instanceof ForeignTripCancelled) {
        setStatus('awaiting')
        return
      }

      setStatus('finish-with-error')
      if (axios.isAxiosError(e)) {
        const data = e.response?.data
        if (Array.isArray(data) && data.length > 0) {
          const { code } = data[0]
          const errorMsg = tSafe(`errorCode.${code}`, tSafe(DEFAULT_ERROR_MSG_KEY))
          toast.error({ text: errorMsg, autoCloseMs: 5000 })
        }
        if (props.onError) props.onError(e)
        return
      }
      toast.error({ text: tSafe(DEFAULT_ERROR_MSG_KEY) })
      console.error(e)
      if (props.onError) props.onError(e)
    },
    onSuccess: (result) => {
      if (result?.partialWarning) {
        const serverCode = axios.isAxiosError(result.adoptError)
          ? (result.adoptError.response?.data as Array<{ code?: string }> | undefined)?.[0]?.code
          : undefined
        const detail = serverCode ? tSafe(`errorCode.${serverCode}`, '') : ''
        toast.warning({
          text: detail
            ? `${tSafe('timesheet.calculation-partial-warning')} ${detail}`
            : tSafe('timesheet.calculation-partial-warning'),
          autoCloseMs: 8000,
        })
      } else {
        toast.success({ text: tSafe('timesheet.calculation-success') })
      }

      // Osobny przypadek od partialWarning: tam drugi przebieg sie wywalil, tu przeliczenie
      // sie udalo, ale nagroda nie domknela wyplaty do celu. Pokazujemy obok, nie zamiast.
      if (result?.nettoMismatchCount) {
        toast.warning({
          text: tSafe('timesheet.netto-mismatch-warning'),
          autoCloseMs: 8000,
        })
      }

      if (props.onSuccess) props.onSuccess(result?.rows ?? null)
    },
    // Przebieg 1 zapisuje kwoty do bazy jeszcze zanim przebieg 2 wystartuje, wiec po bledzie
    // grid MUSI sie odswiezyc - inaczej pokazuje stan sprzed klikniecia, a kolejne "Przelicz"
    // odeslaloby nieaktualne kwoty.
    onSettled: () => {
      setIsAdoptPass(false)
      invalidateQuery()
    },
  })

  const reset = React.useCallback(() => {
    setStatus('awaiting')
    setProgress(null)
    setIsAdoptPass(false)
    m.reset()
  }, [m])

  const baseStatusText = statusTextConfigKeys[status] ? tSafe(statusTextConfigKeys[status]) : ''

  const statusText =
    progress && baseStatusText
      ? `${baseStatusText} ${progress.current}/${progress.total}`
      : baseStatusText
  return { ...m, status, reset, statusText }
}
